import GrayCard from "components/gray-card";
import Modal from "components/modal";
import React, { useMemo, useRef, useState } from "react";
import AddTrackLink from "../modals/addTrackLink";
import styled from "styled-components";
import { Colors } from "utils/colors";
import Typography from "components/typography";
import PrimaryButton from "components/buttons/primary-button";
import { ITrackLink } from "../..";
import { StoreIconName, storeIcons } from "constants/storeIcons";

interface IProps {
  links: ITrackLink[];
  setLinks: (links: ITrackLink[]) => void;
}
const TrackLinks = ({ links, setLinks }: IProps) => {
  const [modalIsOpen, setModalIsOpen] = useState(false);

  const [editIndex, setEditIndex] = useState<number>();

  const SaveLink = (link: ITrackLink) => {
    setLinks([...links, link]);
    setModalIsOpen(false);
  };

  const EditLink = (EditedLink: ITrackLink, index: number) => {
    setLinks(links.map((link, i) => (i === index ? { ...EditedLink } : link)));
    setEditIndex(undefined);
    setModalIsOpen(false);
  };

  const DeleteLink = (index: number) => {
    setLinks(links.filter((_, i) => i !== index));
  };

  //drag logic
  const dragItem = useRef<number | null>(null);
  const dragOverItem = useRef<number | null>(null);

  const dragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    dragItem.current = index;
    e.dataTransfer.effectAllowed = "move"; // Optional: gives user a visual indicator
  };

  const dragEnter = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    e.preventDefault();
    dragOverItem.current = index;
  };

  const dragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault(); // This prevents the entire page from being dragged
  };

  const drop = () => {
    if (dragItem.current === null || dragOverItem.current === null) return;

    const copyListItems = [...links];
    const dragItemContent = copyListItems[dragItem.current];

    copyListItems.splice(dragItem.current, 1);
    copyListItems.splice(dragOverItem.current, 0, dragItemContent);

    dragItem.current = null;
    dragOverItem.current = null;

    setLinks(copyListItems);
  };

  const ButtonItem = ({ name, url, index, logo }: ITrackLink) => {
    return (
      <LinkItemContainer>
        <DragIcon>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="100%"
            height="100%"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M19 10H5"
              stroke="#CDCDCD"
              stroke-width="1.5"
              stroke-linecap="round"
            />
            <path
              d="M19 14H5"
              stroke="#CDCDCD"
              stroke-width="1.5"
              stroke-linecap="round"
            />
            <path
              d="M19 6H5"
              stroke="#CDCDCD"
              stroke-width="1.5"
              stroke-linecap="round"
            />
            <path
              d="M19 18H5"
              stroke="#CDCDCD"
              stroke-width="1.5"
              stroke-linecap="round"
            />
          </svg>
        </DragIcon>
        <LinkIcon>{logo}</LinkIcon>
        <div>
          <Typography size="s" weight="medium" mb="0.4">
            {name}
          </Typography>
          <Typography size="xs" color={Colors.grey500}>
            {url}
          </Typography>
        </div>
        <EditIcon
          onClick={() => {
            setModalIsOpen(true);
            setEditIndex(index);
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="100%"
            height="100%"
            viewBox="0 0 14 14"
            fill="none"
          >
            <path
              d="M8.18004 2.53933L8.64344 2.07589C9.41129 1.30804 10.6562 1.30804 11.4241 2.07589C12.1919 2.84374 12.1919 4.08867 11.4241 4.85653L10.9606 5.31996M8.18004 2.53933C8.18004 2.53933 8.23794 3.52414 9.10689 4.39309C9.97584 5.26203 10.9606 5.31996 10.9606 5.31996M8.18004 2.53933L3.91941 6.79994C3.63083 7.08854 3.48654 7.23279 3.36245 7.39189C3.21606 7.57959 3.09057 7.78264 2.98817 7.99749C2.90136 8.17964 2.83684 8.37324 2.70778 8.76039L2.1609 10.401M10.9606 5.31996L6.70004 9.58059C6.41144 9.86914 6.26719 10.0134 6.10809 10.1375C5.9204 10.2839 5.71734 10.4094 5.50248 10.5118C5.32034 10.5986 5.12675 10.6631 4.73958 10.7922L3.09896 11.3391M2.1609 10.401L2.02722 10.8021C1.96371 10.9926 2.0133 11.2026 2.15531 11.3447C2.29733 11.4867 2.50739 11.5363 2.69792 11.4728L3.09896 11.3391M2.1609 10.401L3.09896 11.3391"
              stroke="#CDCDCD"
              stroke-width="0.874999"
            />
          </svg>
        </EditIcon>
      </LinkItemContainer>
    );
  };
  return (
    <GrayCard title="Track Links">
      <Modal isActive={modalIsOpen} closeModal={() => setModalIsOpen(false)}>
        <AddTrackLink
          addedLinks={links.map((link) => link.name)}
          close={() => setModalIsOpen(false)}
          save={(link) => {
            SaveLink(link);
          }}
          editData={
            typeof editIndex === "number" ? links[editIndex] : undefined
          }
          saveEdit={(link) => {
            if (typeof editIndex === "number") EditLink(link, editIndex);
          }}
        />
      </Modal>
      {links.length > 0 && (
        <LinksGrid>
          {links.map((btn, i) => {
            return (
              <LinkGridItem
                key={i}
                onDragStart={(e) => dragStart(e, i)}
                onDragEnter={(e) => dragEnter(e, i)}
                onDragEnd={drop}
                onDragOver={dragOver}
                draggable
              >
                <ButtonItem
                  key={i}
                  logo={storeIcons[btn.name as StoreIconName]}
                  name={btn.name}
                  url={btn.url}
                  index={i}
                />
                <DeleteIcon onClick={() => DeleteLink(i)}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="100%"
                    height="100%"
                    viewBox="0 0 20 21"
                    fill="none"
                  >
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M18.3327 10.4998C18.3327 15.1022 14.6017 18.8332 9.99935 18.8332C5.39697 18.8332 1.66602 15.1022 1.66602 10.4998C1.66602 5.89746 5.39697 2.1665 9.99935 2.1665C14.6017 2.1665 18.3327 5.89746 18.3327 10.4998ZM13.1243 10.4998C13.1243 10.845 12.8445 11.1248 12.4993 11.1248H7.49935C7.15417 11.1248 6.87435 10.845 6.87435 10.4998C6.87435 10.1547 7.15417 9.87484 7.49935 9.87484H12.4993C12.8445 9.87484 13.1243 10.1547 13.1243 10.4998Z"
                      fill="#F84C4C"
                    />
                  </svg>
                </DeleteIcon>
              </LinkGridItem>
            );
          })}
        </LinksGrid>
      )}
      <PrimaryButton
        variant="text"
        text="Add a Link"
        onClick={() => setModalIsOpen(true)}
        fitContent
        icon={
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="100%"
            height="100%"
            viewBox="0 0 14 14"
            fill="none"
          >
            <path
              d="M6.99935 12.8332C10.221 12.8332 12.8327 10.2215 12.8327 6.99984C12.8327 3.77818 10.221 1.1665 6.99935 1.1665C3.77769 1.1665 1.16602 3.77818 1.16602 6.99984C1.16602 10.2215 3.77769 12.8332 6.99935 12.8332Z"
              stroke="#FEB73D"
              stroke-width="0.875"
            />
            <path
              d="M8.75 7H7M7 7H5.25M7 7V5.25M7 7V8.75"
              stroke="#FEB73D"
              stroke-width="0.875"
              stroke-linecap="round"
            />
          </svg>
        }
        iconPosition="left"
        iconSize="1.4"
      />
    </GrayCard>
  );
};

export default TrackLinks;

const LinksGrid = styled.div`
  display: grid;
  grid-template-columns: 100%;
  grid-row-gap: 2.4rem;
  margin-bottom: 2.4rem;
  overflow: hidden;
`;

const LinkGridItem = styled.div`
  display: grid;
  grid-template-columns: 95% auto;
  justify-content: space-between;
  align-items: center;
`;

const LinkItemContainer = styled.div`
  width: 100%;
  background-color: ${Colors.grey600};
  padding: 2rem 1.2rem;
  border-radius: 0.8rem;
  display: flex;
  align-items: center;
`;

const DragIcon = styled.div`
  width: 2.4rem;
  height: 2.4rem;
  cursor: pointer;
`;

const LinkIcon = styled.div`
  width: 4rem;
  height: 4rem;
  margin-left: 1rem;
  margin-right: 1.6rem;
  object-fit: contain;
`;

const EditIcon = styled.div`
  margin-left: auto;
  cursor: pointer;
  width: 1.4rem;
  height: 1.4rem;
`;

const DeleteIcon = styled.div`
  width: 2rem;
  height: 2rem;
  cursor: pointer;
`;
